import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, UserPlus, MessageCircle, Users, UserCheck, Clock, UserX } from "lucide-react";
import { FriendRequestButton } from "@/components/friends/FriendRequestButton";
import { DirectMessageDialog } from "@/components/messages/DirectMessageDialog";
import { InviteGenerator } from "@/components/invites/InviteGenerator";

interface UserProfile {
  id: string;
  username: string;
  handle: string;
  avatar_url: string;
  bio: string;
}

interface FriendRequest {
  id: string;
  requester_id: string;
  status: string;
  created_at: string;
  message?: string;
  profiles: UserProfile;
}

const Friends = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get current user
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user || null;
    }
  });

  // Get user's friends
  const { data: friends } = useQuery({
    queryKey: ['user-friends', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      const { data: friendIds } = await supabase
        .rpc('get_user_friends', { user_id: currentUser.id });

      if (!friendIds?.length) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, handle, avatar_url, bio')
        .in('id', friendIds.map(f => f.friend_id));

      return profiles || [];
    },
    enabled: !!currentUser?.id
  });

  // Get pending friend requests (received)
  const { data: pendingRequests } = useQuery({
    queryKey: ['pending-friend-requests', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      const { data } = await supabase
        .from('friend_requests')
        .select(`
          id,
          requester_id,
          status,
          created_at,
          message
        `)
        .eq('requested_id', currentUser.id)
        .eq('status', 'pending');

      if (!data?.length) return [];

      // Get the profiles for the requesters
      const requesterIds = data.map(r => r.requester_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, handle, avatar_url, bio')
        .in('id', requesterIds);

      // Combine the requests with profile data
      return data.map(request => ({
        ...request,
        profiles: profiles?.find(p => p.id === request.requester_id)
      })).filter(r => r.profiles);
    },
    enabled: !!currentUser?.id
  });

  // Search users
  const { data: searchResults } = useQuery({
    queryKey: ['search-users', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim() || !currentUser?.id) return [];
      
      const { data } = await supabase
        .from('profiles')
        .select('id, username, handle, avatar_url, bio')
        .or(`username.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%`)
        .neq('id', currentUser.id)
        .limit(10);

      return data || [];
    },
    enabled: !!searchQuery.trim() && !!currentUser?.id
  });

  const handleAcceptRequest = async (requestId: string) => {
    try {
      const { error } = await supabase.rpc('accept_friend_request', {
        request_id: requestId
      });

      if (error) throw error;

      toast({
        title: "Friend request accepted!",
        description: "You are now friends",
      });

      queryClient.invalidateQueries({ queryKey: ['pending-friend-requests'] });
      queryClient.invalidateQueries({ queryKey: ['user-friends'] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeclineRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'declined', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Friend request declined",
        description: "The request has been declined",
      });

      queryClient.invalidateQueries({ queryKey: ['pending-friend-requests'] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
          Friends
        </h1>

        <Tabs defaultValue="friends" className="w-full">
          <TabsList className="grid w-full grid-cols-4 rounded-full bg-white/80 dark:bg-gray-800/80 p-1.5 backdrop-blur-md">
            <TabsTrigger value="friends" className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5">
              <Users className="w-4 h-4 mr-2" />
              Friends ({friends?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5">
              <Clock className="w-4 h-4 mr-2" />
              Requests ({pendingRequests?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="search" className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5">
              <Search className="w-4 h-4 mr-2" />
              Search
            </TabsTrigger>
            <TabsTrigger value="invite" className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite
            </TabsTrigger>
          </TabsList>

          <TabsContent value="friends" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Your Friends
                </CardTitle>
              </CardHeader>
              <CardContent>
                {friends?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No friends yet. Start by searching for people or inviting friends!</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {friends?.map((friend) => (
                      <div key={friend.id} className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={friend.avatar_url} />
                            <AvatarFallback>{friend.username?.[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{friend.username}</p>
                            <p className="text-sm text-muted-foreground">{friend.handle}</p>
                            {friend.bio && (
                              <p className="text-sm text-muted-foreground mt-1">{friend.bio}</p>
                            )}
                          </div>
                        </div>
                        <DirectMessageDialog 
                          recipientId={friend.id}
                          recipientName={friend.username}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Friend Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingRequests?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending friend requests</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {pendingRequests?.map((request) => (
                      <div key={request.id} className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={request.profiles.avatar_url} />
                            <AvatarFallback>{request.profiles.username?.[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{request.profiles.username}</p>
                            <p className="text-sm text-muted-foreground">{request.profiles.handle}</p>
                            {request.message && (
                              <p className="text-sm mt-1 italic">"{request.message}"</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(request.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => handleAcceptRequest(request.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeclineRequest(request.id)}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="search" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Find Friends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Search by username or handle..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {searchResults?.length === 0 && searchQuery.trim() ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No users found for "{searchQuery}"</p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {searchResults?.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-4 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={user.avatar_url} />
                              <AvatarFallback>{user.username?.[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{user.username}</p>
                              <p className="text-sm text-muted-foreground">{user.handle}</p>
                              {user.bio && (
                                <p className="text-sm text-muted-foreground mt-1">{user.bio}</p>
                              )}
                            </div>
                          </div>
                          {currentUser && (
                            <FriendRequestButton 
                              targetUserId={user.id}
                              currentUserId={currentUser.id}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invite" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Invite Friends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <InviteGenerator />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Friends;